import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Company } from '../../production-inventory.model';
import { CompanyFactoryService } from '../../services/company-factory.service';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

@Component({
  selector: 'app-companies',
  standalone: false,
  templateUrl: './companies.component.html',
  styleUrl: './companies.component.scss',
})
export class CompaniesComponent implements OnInit, OnDestroy {
  @ViewChild('companyModal') companyModalRef!: TemplateRef<any>;

  protected companies: Company[] = [];
  protected isDataLoading = false;
  protected isSaving = false;
  protected isEditMode = false;
  protected modalRef: NgbModalRef | null = null;
  protected companyForm!: FormGroup;
  protected formError: string | null = null;

  private editingCompanyId: string | null = null;

  constructor(
    private companyFactoryService: CompanyFactoryService,
    private modalService: NgbModal,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadCompanies();
  }

  private initForm(): void {
    this.companyForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      code: ['', [Validators.maxLength(20)]],
      address: [''],
      description: [''],
      email: ['', [Validators.email]],
      contact_number: [''],
      website: [''],
      gst_number: [''],
    });
  }

  private loadCompanies(): void {
    this.isDataLoading = true;
    this.companyFactoryService
      .listCompanies()
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (companies: Company[]) => {
          this.companies = companies;
        },
      });
  }

  protected onAddCompany(): void {
    this.isEditMode = false;
    this.editingCompanyId = null;
    this.formError = null;
    this.companyForm.reset({
      name: '',
      code: '',
      address: '',
      description: '',
      email: '',
      contact_number: '',
      website: '',
      gst_number: '',
    });

    const options: NgbModalOptions = { backdrop: 'static', size: 'lg' };
    this.modalRef = this.modalService.open(this.companyModalRef, options);
  }

  protected onEditCompany(company: Company): void {
    this.isEditMode = true;
    this.editingCompanyId = company.id;
    this.formError = null;

    this.companyForm.patchValue({
      name: company.name,
      code: company.code || '',
      address: company.address || '',
      description: company.description || '',
      email: company.email || '',
      contact_number: company.contact_number || '',
      website: company.website || '',
      gst_number: company.gst_number || '',
    });

    const options: NgbModalOptions = { backdrop: 'static', size: 'lg' };
    this.modalRef = this.modalService.open(this.companyModalRef, options);
  }

  protected onDeleteCompany(company: Company): void {
    const ref = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = 'Delete Company';
    ref.componentInstance.message = `Are you sure you want to delete "${company.name}"?`;
    ref.componentInstance.warning = 'This action cannot be undone.';

    ref.result.then(() => {
      this.companyFactoryService.deleteCompany(company.id).subscribe({
        next: () => {
          this.companies = this.companies.filter(c => c.id !== company.id);
        },
      });
    });
  }

  protected onSubmit(): void {
    if (this.companyForm.invalid) return;

    this.isSaving = true;
    this.formError = null;

    const v = this.companyForm.value;
    const codeVal = v.code?.trim().toUpperCase() || '';
    const payload: any = {
      name: v.name.trim(),
      address: v.address?.trim() || '',
      description: v.description?.trim() || '',
      email: v.email?.trim() || '',
      contact_number: v.contact_number?.trim() || '',
      website: v.website?.trim() || '',
      gst_number: v.gst_number?.trim() || '',
    };
    if (codeVal) payload.code = codeVal;

    if (this.isEditMode && this.editingCompanyId) {
      this.companyFactoryService
        .updateCompany(this.editingCompanyId, payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (updated: Company) => {
            const idx = this.companies.findIndex(c => c.id === updated.id);
            if (idx !== -1) this.companies[idx] = updated;
            this.modalRef?.dismiss();
          },
          error: () => {
            this.formError = 'Failed to update company. Please try again.';
          },
        });
    } else {
      this.companyFactoryService
        .createCompany(payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (created: Company) => {
            this.companies = [...this.companies, created];
            this.modalRef?.dismiss();
          },
          error: () => {
            this.formError = 'Failed to create company. Please try again.';
          },
        });
    }
  }

  protected isInvalid(field: string): boolean {
    const ctrl = this.companyForm.get(field);
    return (ctrl?.invalid && (ctrl.touched || ctrl.dirty)) ?? false;
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
  }
}
